import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
    PhoneMissed, Loader2, User, UserCheck, ChevronRight, Clock, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
    { id: 'today', label: "Aujourd'hui" },
    { id: 'week',  label: 'Cette semaine' },
    { id: 'month', label: 'Ce mois' },
    { id: 'all',   label: 'Tout' },
];

function fmt(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        if (sameDay) return `Aujourd'hui ${time}`;
        return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} ${time}`;
    } catch { return '—'; }
}

export default function MissedNotRecalledModal({ open, onOpenChange, onShowHistory }) {
    const navigate = useNavigate();
    const [period, setPeriod] = useState('month');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/api/calls/missed-not-recalled?period=${period}`, { withCredentials: true });
                if (!cancelled) setItems(res.data?.items || []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, period]);

    const goToContact = (m) => {
        if (!m) return;
        onOpenChange(false);
        if (m.type === 'client' && m.id) navigate(`/clients/${m.id}`);
        else if (m.type === 'prospect' && m.id) navigate(`/prospects/${m.id}`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="missed-not-recalled-modal">
                <DialogHeader className="px-6 pt-6 pb-3 bg-gradient-to-r from-orange-500 to-red-500">
                    <DialogTitle className="text-white text-lg font-bold flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Manqués non rappelés
                    </DialogTitle>
                    <p className="text-white/90 text-xs mt-1">
                        Numéros qui ont essayé de vous joindre mais que vous n'avez pas encore rappelés.
                    </p>
                </DialogHeader>

                {/* Period filter */}
                <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-200 flex gap-1">
                    {PERIODS.map(p => (
                        <button
                            key={p.id}
                            onClick={() => setPeriod(p.id)}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${period === p.id ? 'bg-[#C9A962]/15 text-[#C9A962]' : 'text-gray-500 hover:bg-white'}`}
                            data-testid={`mnr-period-${p.id}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <div className="max-h-[60vh] overflow-y-auto" data-testid="missed-not-recalled-list">
                    {loading && (
                        <div className="px-6 py-12 flex items-center justify-center text-gray-400 gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Chargement…
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="px-6 py-12 text-center">
                            <PhoneMissed className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-500">
                                Bravo, vous avez rappelé tous vos appels manqués sur cette période !
                            </p>
                        </div>
                    )}
                    {!loading && items.map(it => (
                        <div
                            key={it.tail9}
                            className="px-6 py-3 border-b border-gray-50 hover:bg-orange-50/40 transition-colors"
                            data-testid={`mnr-item-${it.tail9}`}
                        >
                            <div className="flex items-center gap-3">
                                <div className="shrink-0 w-9 h-9 rounded-full bg-red-50 flex items-center justify-center relative">
                                    <PhoneMissed className="w-4 h-4 text-red-500" />
                                    {it.missed_count > 1 && (
                                        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                                            {it.missed_count}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => onShowHistory?.(it.number)}
                                    className="flex-1 min-w-0 text-left"
                                    data-testid={`mnr-history-${it.tail9}`}
                                >
                                    <p className="text-sm font-mono text-[#1B365D] hover:text-[#C9A962] truncate">
                                        {it.number}
                                    </p>
                                    <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                                        <Clock className="w-3 h-3" />
                                        Dernier manqué : <span className="font-semibold text-red-600">{fmt(it.last_missed_at)}</span>
                                        <span className="text-gray-300">·</span>
                                        ligne {it.phone_line || '—'}
                                    </p>
                                </button>
                                {it.match ? (
                                    <button
                                        onClick={() => goToContact(it.match)}
                                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#C9A962]/10 hover:bg-[#C9A962]/20 transition-colors group"
                                    >
                                        {it.match.type === 'client'
                                            ? <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                                            : <User className="w-3.5 h-3.5 text-purple-500" />}
                                        <span className="text-xs font-semibold text-[#1B365D] truncate max-w-[140px]">{it.match.nom}</span>
                                        <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-[#C9A962]" />
                                    </button>
                                ) : (
                                    <span className="shrink-0 text-[11px] text-gray-400 italic">Inconnu</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {!loading && items.length > 0 && (
                    <div className="px-6 py-2.5 bg-gray-50 border-t border-gray-100 text-center text-xs text-gray-500">
                        {items.length} numéro{items.length > 1 ? 's' : ''} en attente de rappel
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
